/**
 * InvoiceDetailPage
 * Tela de visualização de uma fatura — segue a receita de `EmployeeDetailsPage`:
 * identidade → destaques → blocos temáticos → itens relacionados.
 *
 * @version 2025.1.0
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Typography,
  Box,
  Chip,
  CircularProgress,
  Button,
  Grid,
  useTheme,
} from '@mui/material';
import {
  ArrowBack,
  Lock,
  LockOpen,
  Payment,
  Cancel,
  Receipt,
  PictureAsPdf,
  Payments as PaymentsIcon,
  Event as EventIcon,
  CalendarMonth as PeriodIcon,
  ListAlt as ItemsIcon,
  Business as BusinessIcon,
  ReceiptLong as ReceiptLongIcon,
  Notes as NotesIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  PageShell,
  DataTable,
  IdentityBanner,
  IdentifierChip,
  SectionCard,
  InfoItem,
  StatCard,
  type DataTableColumn,
} from '../../components';
import { invoiceService, Invoice, InvoiceItem } from '../../services/invoiceService';
import { falhaDeCarga, textoDaFalhaDeCarga, type FalhaDeCarga } from '../../utils/acessoNegado';
import { AvisoDeFalhaAoCarregar } from '../../components/AvisoDeFalhaAoCarregar';
import { ActionButton } from '../../components/common';
import { useAcao } from '../../authorization/useVeredito';
import { InvoiceItemDialog, type ModoDaCorrecao } from './InvoiceItemDialog';
import { InvoiceItemChangesBlock } from './InvoiceItemChangesBlock';
import {
  formatCurrency,
  STATUS_LABELS,
  STATUS_COLORS,
  TYPE_LABELS,
  TYPE_COLORS,
  formatPeriod,
} from '../../utils/financialHelpers';

const itemColumns: DataTableColumn<InvoiceItem>[] = [
  { id: 'description', label: 'Descrição', primary: true, render: (item) => item.description },
  {
    id: 'unitPrice',
    label: 'Preço Unitário',
    align: 'right',
    render: (item) => formatCurrency(item.unitPrice),
  },
  { id: 'quantity', label: 'Quantidade', align: 'right', render: (item) => item.quantity },
  {
    id: 'subtotal',
    label: 'Subtotal',
    align: 'right',
    render: (item) => formatCurrency(item.subtotal),
  },
];

const COR_DOS_ITENS = '#00897B';

const formatDueDate = (dueDate: string | null): string =>
  dueDate ? new Date(dueDate + 'T00:00:00').toLocaleDateString('pt-BR') : '';

// ============================================================================
// COMPONENT
// ============================================================================

const InvoiceDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [falha, setFalha] = useState<FalhaDeCarga | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [correcao, setCorrecao] = useState<{ modo: ModoDaCorrecao; item: InvoiceItem } | null>(null);
  const [versaoDoHistorico, setVersaoDoHistorico] = useState(0);

  const editarItem = useAcao('invoices.items.manage', 'Editar item');
  const removerItem = useAcao('invoices.items.manage', 'Remover item');

  // Releitura com a fatura na tela: o aviso não aparece, então a falha vai para o toast.
  const faturaNaTela = useRef(false);

  /** Silencioso recarrega sem trocar a tela pelo carregando: o total muda no lugar. */
  const loadData = useCallback(async (silencioso = false) => {
    if (!id) return;
    if (!silencioso) setLoading(true);
    setFalha(null);
    try {
      const data = await invoiceService.getInvoiceById(Number(id));
      setInvoice(data);
      faturaNaTela.current = true;
    } catch (err: unknown) {
      const falhaAoCarregar = falhaDeCarga(err, 'Não foi possível carregar a fatura. Tente de novo.');
      setFalha(falhaAoCarregar);
      if (faturaNaTela.current || (falhaAoCarregar.status !== 403 && falhaAoCarregar.status !== 404)) {
        enqueueSnackbar(textoDaFalhaDeCarga(falhaAoCarregar, 'A fatura não foi encontrada.'), {
          variant: 'error',
        });
      }
    } finally {
      setLoading(false);
    }
  }, [id, enqueueSnackbar]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAction = async (
    action: 'close' | 'reopen' | 'pay' | 'cancel',
    confirmMsg: string,
  ) => {
    if (!invoice || !window.confirm(confirmMsg)) return;

    setActionLoading(true);
    try {
      switch (action) {
        case 'close':
          await invoiceService.closeInvoice(invoice.id);
          enqueueSnackbar('Fatura fechada com sucesso!', { variant: 'success' });
          break;
        case 'reopen':
          await invoiceService.reopenInvoice(invoice.id);
          enqueueSnackbar('Fatura reaberta com sucesso!', { variant: 'success' });
          break;
        case 'pay':
          await invoiceService.markAsPaid(invoice.id);
          enqueueSnackbar('Fatura marcada como paga!', { variant: 'success' });
          break;
        case 'cancel':
          await invoiceService.cancelInvoice(invoice.id);
          enqueueSnackbar('Fatura cancelada!', { variant: 'success' });
          break;
      }
      loadData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao executar ação';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!invoice) return;
    setPdfLoading(true);
    try {
      await invoiceService.downloadPdf(invoice.id);
    } catch (err: unknown) {
      enqueueSnackbar(
        err instanceof Error ? err.message : 'Erro ao gerar PDF da fatura',
        { variant: 'error' },
      );
    } finally {
      setPdfLoading(false);
    }
  };

  const isOverdue =
    !!invoice &&
    invoice.status === 'CLOSED' &&
    !!invoice.dueDate &&
    new Date(invoice.dueDate) < new Date(new Date().toISOString().slice(0, 10));

  const items = invoice?.items ?? [];

  const handleItemCorrigido = (modo: ModoDaCorrecao) => {
    setCorrecao(null);
    enqueueSnackbar(modo === 'remover' ? 'Item removido da fatura.' : 'Item da fatura atualizado.', {
      variant: 'success',
    });
    setVersaoDoHistorico((versao) => versao + 1);
    loadData(true);
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/home' },
        { label: 'Financeiro', path: '/financial' },
        { label: 'Faturas', path: '/financial/invoices' },
        { label: invoice ? `#${invoice.id}` : 'Detalhes' },
      ]}
      actions={
        <>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/financial/invoices')}
          >
            Voltar
          </Button>

          {invoice && (
            <>
              <Button
                variant="outlined"
                size="small"
                startIcon={pdfLoading ? <CircularProgress size={18} /> : <PictureAsPdf />}
                disabled={pdfLoading}
                onClick={handleDownloadPdf}
              >
                Baixar PDF
              </Button>

              {invoice.status === 'OPEN' && (
                <ActionButton
                  acao="invoices.manage"
                  rotulo="Fechar Fatura"
                  variant="contained"
                  size="small"
                  color="info"
                  startIcon={<Lock />}
                  disabled={actionLoading}
                  onClick={() => handleAction('close', 'Deseja fechar esta fatura?')}
                >
                  Fechar Fatura
                </ActionButton>
              )}

              {invoice.status === 'CLOSED' && (
                <>
                  <ActionButton
                    acao="invoices.manage"
                    rotulo="Reabrir"
                    variant="outlined"
                    size="small"
                    color="warning"
                    startIcon={<LockOpen />}
                    disabled={actionLoading}
                    onClick={() => handleAction('reopen', 'Deseja reabrir esta fatura?')}
                  >
                    Reabrir
                  </ActionButton>
                  <ActionButton
                    acao="invoices.manage"
                    rotulo="Marcar como Paga"
                    variant="contained"
                    size="small"
                    color="success"
                    startIcon={<Payment />}
                    disabled={actionLoading}
                    onClick={() => handleAction('pay', 'Deseja marcar esta fatura como paga?')}
                  >
                    Marcar como Paga
                  </ActionButton>
                </>
              )}

              {(invoice.status === 'OPEN' || invoice.status === 'CLOSED') && (
                <ActionButton
                  acao="invoices.manage"
                  rotulo="Cancelar Fatura"
                  variant="outlined"
                  size="small"
                  color="error"
                  startIcon={<Cancel />}
                  disabled={actionLoading}
                  onClick={() =>
                    handleAction(
                      'cancel',
                      'Tem certeza que deseja cancelar esta fatura? Esta ação não pode ser desfeita.',
                    )
                  }
                >
                  Cancelar Fatura
                </ActionButton>
              )}
            </>
          )}
        </>
      }
    >
      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Invoice Details */}
      {!loading && invoice && (
        <>
          {/* ═══ 1. Identidade ═══ */}
          <IdentityBanner
            avatar={<Receipt sx={{ fontSize: 32 }} />}
            name={`Fatura #${invoice.id}`}
            status={
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={TYPE_LABELS[invoice.type] || invoice.type}
                  color={TYPE_COLORS[invoice.type] || 'default'}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  label={STATUS_LABELS[invoice.status] || invoice.status}
                  color={STATUS_COLORS[invoice.status] || 'default'}
                  size="small"
                />
                {isOverdue && <Chip label="Vencida" size="small" color="error" />}
              </Box>
            }
            context={`${invoice.company?.tradeName || invoice.company?.legalName || '—'} › ${
              invoice.provider?.tradeName || invoice.provider?.legalName || 'Sem prestador'
            }`}
            identifiers={
              <IdentifierChip
                icon={<PeriodIcon sx={{ fontSize: 16 }} />}
                label={formatPeriod(invoice.referenceMonth, invoice.referenceYear)}
              />
            }
          />

          {/* ═══ 2. Destaques ═══ */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <StatCard
                icon={<PaymentsIcon fontSize="small" />}
                label="Valor Total"
                value={formatCurrency(invoice.totalAmount)}
                color={theme.palette.primary.main}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <StatCard
                icon={<EventIcon fontSize="small" />}
                label="Vencimento"
                value={formatDueDate(invoice.dueDate) || '—'}
                sublabel={isOverdue ? 'Vencida' : undefined}
                color={isOverdue ? theme.palette.error.main : theme.palette.warning.main}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <StatCard
                icon={<PeriodIcon fontSize="small" />}
                label="Período de Referência"
                value={formatPeriod(invoice.referenceMonth, invoice.referenceYear)}
                color={theme.palette.info.main}
              />
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <StatCard
                icon={<ItemsIcon fontSize="small" />}
                label="Itens"
                value={String(items.length)}
                color="#00897B"
              />
            </Grid>
          </Grid>

          {/* ═══ 3. Blocos temáticos ═══ */}
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionCard
                title="Empresa & Prestador"
                icon={<BusinessIcon fontSize="small" />}
                accentColor={theme.palette.success.main}
              >
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem
                      label="Empresa"
                      value={invoice.company?.tradeName || invoice.company?.legalName}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem
                      label="Prestador"
                      value={invoice.provider?.tradeName || invoice.provider?.legalName}
                    />
                  </Grid>
                </Grid>
              </SectionCard>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <SectionCard
                title="Observações"
                icon={<NotesIcon fontSize="small" />}
                accentColor="#546E7A"
              >
                <InfoItem label="Observações" value={invoice.notes} fullWidth />
              </SectionCard>
            </Grid>
          </Grid>

          {/* ═══ 4. Relacionados ═══ */}
          <SectionCard
            title="Itens da Fatura"
            icon={<ReceiptLongIcon fontSize="small" />}
            accentColor={COR_DOS_ITENS}
            badge={<Chip size="small" label={items.length} />}
          >
            {items.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nenhum registro encontrado
              </Typography>
            ) : (
              <>
                <DataTable
                  columns={itemColumns}
                  rows={items}
                  getRowId={(item) => item.id}
                  actions={
                    invoice.status === 'OPEN'
                      ? [
                          {
                            icon: <EditIcon fontSize="small" />,
                            tooltip: editarItem.tooltip,
                            disabled: editarItem.disabled,
                            onClick: (item) => setCorrecao({ modo: 'editar', item }),
                          },
                          {
                            icon: <DeleteIcon fontSize="small" />,
                            tooltip: removerItem.tooltip,
                            color: 'error',
                            disabled: removerItem.disabled,
                            onClick: (item) => setCorrecao({ modo: 'remover', item }),
                          },
                        ]
                      : undefined
                  }
                  emptyState={{
                    title: 'Nenhum registro encontrado',
                    description:
                      'Os itens vêm dos atendimentos e serviços faturados no período desta fatura.',
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2,
                    px: 2,
                    pt: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    Total
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {formatCurrency(invoice.totalAmount)}
                  </Typography>
                </Box>
              </>
            )}
          </SectionCard>

          <Box sx={{ mt: 3 }}>
            <InvoiceItemChangesBlock
              invoiceId={invoice.id}
              versao={versaoDoHistorico}
              accentColor={COR_DOS_ITENS}
            />
          </Box>

          {correcao && (
            <InvoiceItemDialog
              invoiceId={invoice.id}
              item={correcao.item}
              modo={correcao.modo}
              onClose={() => setCorrecao(null)}
              onSaved={handleItemCorrigido}
            />
          )}
        </>
      )}

      {/* Falha ao abrir (sem acesso, inexistente ou erro) */}
      {!loading && !invoice && (
        <AvisoDeFalhaAoCarregar
          mensagem={textoDaFalhaDeCarga(falha, 'Fatura não encontrada.')}
          rotuloDoBotao="Voltar para faturas"
          aoVoltar={() => navigate('/financial/invoices')}
        />
      )}
    </PageShell>
  );
};

export default InvoiceDetailPage;
